import ButtonShadow from 'components/ButtonShadow/ButtonShadow'
import Dialog from 'components/Dialog/Dialog'
import React, { useCallback, useEffect, useState } from 'react'
import { getPodcastPath } from 'Routes/RouteNames'
import { getPodcastRecommendation } from 'services/api'
import { extractSlateText } from 'utils/extractSlateText'
import * as Styled from './PodcastRecommendationModal.styled'

interface PodcastRecommendation {
  podcastId: string
  title: string
  image: string
  description?: string
}

interface PodcastRecommendationCardProps {
  podcastId: string
  canOpen: boolean
  canFetch: boolean
}

const PodcastRecommendationModal: React.FC<PodcastRecommendationCardProps> = ({
  podcastId,
  canOpen,
  canFetch,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, hasHasError] = useState(false)
  const [recommendation, setRecommendation] =
    useState<PodcastRecommendation | null>(null)
  const [isOpen, setIsOpen] = useState(false)

  const fetchPodcastRecommendation = useCallback(async () => {
    setIsLoading(true)
    hasHasError(false)

    const podcastRecommendationOrError = await getPodcastRecommendation(
      podcastId,
    )

    if (podcastRecommendationOrError.hasFailed()) {
      setIsLoading(false)
      hasHasError(true)
      return
    }

    setIsLoading(false)
    setRecommendation(podcastRecommendationOrError.data)
  }, [podcastId])

  const handleCloseModal = useCallback(() => {
    sessionStorage.removeItem('shouldShowRecommendedPodcast')
    setIsOpen(false)
  }, [])

  useEffect(() => {
    if (!canFetch) return
    fetchPodcastRecommendation()
  }, [canFetch, fetchPodcastRecommendation])

  useEffect(() => {
    setIsOpen(canOpen)
  }, [canOpen])

  if (hasError || !recommendation || isLoading || !canOpen) {
    return <></>
  }

  return (
    <Dialog
      isDialogOpen={isOpen}
      handleCloseDialog={handleCloseModal}
      dialogText={
        <Styled.Container>
          <Styled.Title>
            Apoie e tenha conteúdos exclusivos também de
          </Styled.Title>

          <Styled.PodcastContainer>
            <Styled.PodcastImage
              src={recommendation.image}
              alt={`Capa do podcast "${recommendation.title}"`}
            />

            <Styled.PodcastInfoContainer>
              <Styled.PodcastTitle>{recommendation.title}</Styled.PodcastTitle>

              {recommendation.description && (
                <Styled.PodcastDescription>
                  {extractSlateText(recommendation.description)}
                </Styled.PodcastDescription>
              )}
            </Styled.PodcastInfoContainer>
          </Styled.PodcastContainer>

          <Styled.ActionsContainer>
            <ButtonShadow
              label="Voltar"
              onPress={handleCloseModal}
              variant="secondary"
            />
            <ButtonShadow
              label="Conhecer"
              onPress={() => {
                sessionStorage.removeItem('shouldShowRecommendedPodcast')
                /**
                 * The PodcastContent component does not handle podcastId changes
                 * so when navigating to a different podcast, the content is not updated.
                 * To avoid refactoring the PodcastContent right now, we use this workaround.
                 */
                window.location.href = getPodcastPath(recommendation.podcastId)
              }}
            />
          </Styled.ActionsContainer>
        </Styled.Container>
      }
    />
  )
}

export default PodcastRecommendationModal
